import { Box, EditIcon, GKButton, Typography } from "components";
import dayjs from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";
import { useAppSelector } from "store/store";
import type { MFOrderFormValues } from "./useMFOrders";

interface Props {
  onEdit: () => void;
}

const InfoColumn = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
      {label}
    </Typography>
    <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
      {value || "-"}
    </Typography>
  </Box>
);

const formatFrequency = (f: string | null | undefined) =>
  f ? f.charAt(0).toUpperCase() + f.slice(1).replace(/_/g, " ") : "-";

const planTypeLabel = (orderType: string, transactionType: string) => {
  if (transactionType === "lumpsum") {
    return `${orderType.charAt(0).toUpperCase() + orderType.slice(1)} - Lumpsum`;
  }
  const planMap: Record<string, string> = {
    sip: "Plan (SIP)",
    swp: "Plan (SWP)",
    stp: "Plan (STP)",
  };
  return `${orderType.charAt(0).toUpperCase() + orderType.slice(1)} - ${
    planMap[transactionType] ?? transactionType
  }`;
};

const planShort = (transactionType: string) =>
  transactionType === "swp" ? "SWP" : transactionType === "stp" ? "STP" : "SIP";

const OrderSummaryCard = ({ onEdit }: Props) => {
  const { control } = useFormContext<MFOrderFormValues>();
  const values = useWatch({ control });
  const companies = useAppSelector((state) => state.app.companies);

  const orderType = values.orderType ?? "";
  const transactionType = values.transactionType ?? "";
  const isPlan =
    transactionType === "sip" ||
    transactionType === "swp" ||
    transactionType === "stp";
  const isSwitch = orderType === "switch";

  const switchOutName =
    companies?.find((c: any) => c?.gridCode === values.gcode)?.name ??
    values.gcode ??
    "-";
  const switchInName =
    companies?.find((c: any) => c?.gridCode === values.switchInGcode)?.name ??
    values.switchInGcode ??
    "-";

  return (
    <Box
      sx={{
        bgcolor: "var(--grey-bg-100)",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        p: "20px",
        position: "relative",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography color="var(--black-900)" fontSize={14} fontWeight={700}>
          {planTypeLabel(orderType, transactionType)}
        </Typography>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<EditIcon height={14} width={14} />}
          onClick={onEdit}
          sx={{
            fontSize: 12,
            fontWeight: 600,
            height: 32,
            px: "12px",
            textTransform: "none",
          }}
        >
          Edit Details
        </GKButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: "32px",
          gridTemplateColumns: isPlan
            ? isSwitch
              ? "repeat(6, minmax(120px, max-content))"
              : "repeat(5, minmax(120px, max-content))"
            : isSwitch
              ? "repeat(4, minmax(120px, max-content))"
              : "repeat(3, minmax(120px, max-content))",
        }}
      >
        {isSwitch ? (
          <>
            <InfoColumn label="Switch Out Scheme" value={switchOutName} />
            <InfoColumn label="Switch In Scheme" value={switchInName} />
          </>
        ) : (
          <InfoColumn label="Scheme Name" value={switchOutName} />
        )}

        {isPlan ? (
          <>
            <InfoColumn
              label={`${planShort(transactionType)} Frequency`}
              value={formatFrequency(values.frequency)}
            />
            <InfoColumn
              label="Day of Month"
              value={
                values.dayOfMonth != null ? String(values.dayOfMonth) : "-"
              }
            />
            <InfoColumn
              label="No. of Installments"
              value={
                values.isPerpetual
                  ? "Perpetual"
                  : values.installments != null
                    ? String(values.installments)
                    : "-"
              }
            />
            <InfoColumn
              label="Order Placed Date"
              value={
                values.startDate
                  ? dayjs(values.startDate).format("DD/MM/YYYY")
                  : values.scheduledFor
                    ? dayjs(values.scheduledFor).format("DD/MM/YYYY")
                    : "-"
              }
            />
          </>
        ) : (
          <InfoColumn
            label="Order Placed Date"
            value={
              values.scheduledFor
                ? dayjs(values.scheduledFor).format("DD/MM/YYYY")
                : "-"
            }
          />
        )}
      </Box>
    </Box>
  );
};

export default OrderSummaryCard;
